// src/pages/dashboard/Programs.tsx
import { useEffect, useState, useRef, useMemo } from "react";
import { getSupabase, currentUser } from "@/lib/supabase";
import { ENV } from "@/lib/env";
import { useSubscription } from "@/hooks/useSubscription";
import { format, addDays, startOfWeek, isSameDay, parseISO } from "date-fns";
import { 
 Loader2, 
  ChevronLeft, 
  ChevronRight, 
  PlusCircle as AddIcon, 
  CheckCircle, 
  XCircle as CloseIcon, 
  ChevronDown, 
  MoreHorizontal,
  X, 
  TrendingUp,
  AlertCircle,
  Calendar
} from "lucide-react";
import { Link } from "react-router-dom";
import TrainingProgramLoader from "@/components/TrainingProgramLoader";
import WorkoutStatsPanel from "@/components/WorkoutStatsPanel";

// -----------------------------
// PB DEFINITIONS (FRONTEND ONLY)
// -----------------------------
type PbType = "weight" | "power" | "time" | "display";

const PB_DEFINITIONS: Record<string, { type: PbType; unit?: string }> = {
  // Barbell lifts
  back_squat_pb: { type: "weight", unit: "kg" },
  front_squat_pb: { type: "weight", unit: "kg" },
  overhead_squat_pb: { type: "weight", unit: "kg" },
  bench_press_pb: { type: "weight", unit: "kg" },
  overhead_press_pb: { type: "weight", unit: "kg" },
  deadlift_pb: { type: "weight", unit: "kg" },
  snatch_pb: { type: "weight", unit: "kg" },
  clean_jerk_pb: { type: "weight", unit: "kg" },

  // Power
  ftp_pb: { type: "power", unit: "w" },

  // Time
  fran_pb: { type: "time" },
  "5km_pb": { type: "time" },

  // Display only
  vo2max_pb: { type: "display" },
  body_fat_pb: { type: "display" },
};

function parsePbToNumber(pbKey: string, raw: string): number | null {
  const def = PB_DEFINITIONS[pbKey];
  if (!def) return null;

  switch (def.type) {
    case "weight":
    case "power": {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }

    case "time": {
      // MM:SS → seconds
      if (raw.includes(":")) {
        const [m, s] = raw.split(":").map(Number);
        if (Number.isNaN(m) || Number.isNaN(s)) return null;
        return m * 60 + s;
      }
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }

    default:
      return null;
  }
}

// ——————————————————————————————————
// CALENDAR STRIP COMPONENT
// ——————————————————————————————————
interface CalendarStripProps {
  currentDate: Date;
  onSelectDate: (date: Date) => void;
  workouts: any[];
  program: any;
}

const CalendarStrip = ({ currentDate, onSelectDate, workouts, program }: CalendarStripProps) => {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  
  // Get the Monday of the current week
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  
  // Generate dates for the week
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  
  // Create a map of dates to workouts for quick lookup
  const workoutsByDate = useMemo(() => {
    const map = new Map<string, any>();
    workouts.forEach(w => {
      if (w.date) {
        const dateKey = format(parseISO(w.date), "yyyy-MM-dd");
        map.set(dateKey, w);
      }
    });
    return map;
  }, [workouts]);

  // Check if a workout is completed (all sections done)
  const isWorkoutCompleted = (workout: any) => {
    if (!workout?.sections?.length) return false;
    return workout.sections.every((s: any) => s.completed);
  };

  return (
    <div className="bg-gradient-to-br from-[#f8fafc] to-[#ffffff] rounded-2xl border border-[#e2e8f0] p-4 md:p-6 mb-6 md:mb-8">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <h4 className="text-xs md:text-sm text-gray-500 uppercase tracking-wider font-semibold">
            Week {program?.current_week || 1} of {program?.weeks_total || 12}
          </h4>
        </div>
        <span className="px-3 py-1.5 bg-blue-500/15 text-blue-400 rounded-full text-xs font-semibold border border-blue-500/30">
          {program?.sport || "Training"}
        </span>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2 md:gap-3">
        {weekDates.map((date, i) => {
          const dateKey = format(date, "yyyy-MM-dd");
          const workout = workoutsByDate.get(dateKey);
          const isSelected = isSameDay(date, currentDate);
          const hasWorkout = !!workout;
          const isCompleted = hasWorkout && isWorkoutCompleted(workout);
          const isPast = date < new Date() && !isSameDay(date, new Date());

          return (
            <button
              key={dateKey}
              onClick={() => onSelectDate(date)}
              className={`
                relative p-2 md:p-3 rounded-xl md:rounded-2xl transition-all duration-200
                ${isSelected 
                  ? "bg-green-500/10 border-2 border-green-500" 
                  : isCompleted
                    ? "bg-green-500/5 border border-[#e2e8f0]"
                    : "bg-[#ffffff] border border-[#e2e8f0] hover:border-[#e2e8f0]"
                }
              `}
            >
              {/* Day Abbreviation */}
              <p className={`
                text-[10px] md:text-xs font-medium mb-1
                ${isSelected ? "text-green-400" : "text-gray-500"}
              `}>
                {days[i]}
              </p>
              
              {/* Date Number */}
              <p className={`
                text-base md:text-xl font-bold
                ${isSelected 
                  ? "text-green-400" 
                  : isCompleted 
                    ? "text-green-400"
                    : hasWorkout 
                      ? "text-[#0f172a]" 
                      : "text-gray-600"
                }
              `}>
                {format(date, "d")}
              </p>

              {/* Workout Indicator Dot */}
              {hasWorkout && (
                <div className={`
                  w-1.5 h-1.5 md:w-2 md:h-2 rounded-full mx-auto mt-1.5 md:mt-2
                  ${isCompleted ? "bg-green-500" : "bg-blue-500"}
                `} />
              )}
            </button>
          );
        })}
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#e2e8f0]">
        <button
          onClick={() => onSelectDate(addDays(weekStart, -7))}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Previous Week</span>
        </button>
        
        <p className="text-sm text-gray-500">
          {format(weekStart, "MMM d")} — {format(addDays(weekStart, 6), "MMM d, yyyy")}
        </p>
        
        <button
          onClick={() => onSelectDate(addDays(weekStart, 7))}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition text-sm"
        >
          <span className="hidden sm:inline">Next Week</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ——————————————————————————————————
// MAIN PROGRAMS PAGE (Centered with PB Panel)
// ——————————————————————————————————
export default function Programs() {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [pageDataLoaded, setPageDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [program, setProgram] = useState<any>(null);
  const [workouts, setWorkouts] = useState<any[]>([]);
  const [pbs, setPbs] = useState<{ id?: string; key: string; value: string }[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [primarySport, setPrimarySport] = useState<string>("General Fitness");
  const [tempPbInputs, setTempPbInputs] = useState<Record<string, string>>({});
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string>("");
  const [hasGarminConnected, setHasGarminConnected] = useState(false);
  const [sectionNotes, setSectionNotes] = useState<Record<number, string>>({});
  const [syncedWeeks, setSyncedWeeks] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showProgramMenu, setShowProgramMenu] = useState(false);
  const [workoutActivity, setWorkoutActivity] = useState<any>(null);
  const { canAccessPremium, isLoading: subscriptionLoading } = useSubscription();

  // PB Panel States
  const [pbPanelOpen, setPbPanelOpen] = useState(false);
  const [editingPb, setEditingPb] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [addingPb, setAddingPb] = useState(false);
  const [newPbKey, setNewPbKey] = useState("");
  const [newPbValue, setNewPbValue] = useState("");

  const PB_LABELS: Record<string, string> = {
    // Powerlifting / Barbell
    back_squat_pb: "Back Squat",
    front_squat_pb: "Front Squat",
    overhead_squat_pb: "Overhead Squat",
    bench_press_pb: "Bench Press",
    overhead_press_pb: "Overhead Press",
    deadlift_pb: "Deadlift",
    snatch_pb: "Snatch",
    clean_jerk_pb: "Clean & Jerk",

    // Cycling
    ftp_pb: "FTP",
    "20min_power_pb": "20-min Power",
    "5min_power_pb": "5-min Power",

    // Running
    "1km_pb": "1 km",
    "5km_pb": "5 km",
    "10km_pb": "10 km",
    half_marathon_pb: "Half Marathon",
    marathon_pb: "Marathon",

    // Swimming
    "100m_pb": "100 m",
    "400m_pb": "400 m",
    "1500m_pb": "1500 m",

    // Benchmark workouts
    fran_pb: "Fran",
    murph_pb: "Murph",
    grace_pb: "Grace",

    // Other / General
    vo2max_pb: "VO₂ Max",
    resting_hr_pb: "Resting Heart Rate",
    body_fat_pb: "Body Fat %",
    bodyweight_pb: "Bodyweight",
    lean_mass_pb: "Lean Body Mass",

    // Triathlon / Hyrox
    sprint_tri_pb: "Sprint Triathlon",
    olympic_tri_pb: "Olympic Triathlon",
    "70.3_pb": "Half Ironman (70.3)",
    ironman_pb: "Ironman",
    hyrox_pb: "Hyrox",
  };

  const pbValuesByKey = useMemo(() => {
    const map: Record<string, string> = {};
    pbs.forEach(pb => {
      map[pb.key] = pb.value;
    });
    return map;
  }, [pbs]);

  const pbChannelRef = useRef<any>(null);

  const [openSections, setOpenSections] = useState<{ [key: number]: boolean }>({});
  const toggleDetails = (index: number) => {
    setOpenSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSyncToGarmin = async () => {
    if (!program) return;
    if (!hasGarminConnected) {
      alert("Please connect your Garmin account in Settings first.");
      return;
    }

    setSyncing(true);
    setSyncStatus("Compiling & syncing workouts to Garmin...");

    try {
      const weekToSync = program.current_week;

      const compileEndpoints: Record<string, string> = {
        running: "/compile-program-week-to-canonical",
        cycling: "/compile-cycling-week-to-canonical",
        swimming: "/compile-swimming-week-to-canonical",
      };

      const compileEndpoint = compileEndpoints[program.sport.toLowerCase()] 
        || "/compile-program-week-to-canonical";

      console.log(`Syncing ${program.sport} week ${weekToSync} using endpoint: ${compileEndpoint}`);

      const compileRes = await fetch(`${ENV.EDGE_BASE}${compileEndpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          program_id: program.id,
          week: weekToSync,
        }),
      });

      if (!compileRes.ok) {
        let errMsg = `Compile failed (status ${compileRes.status})`;
        try {
          const errBody = await compileRes.json();
          errMsg = errBody.error || errMsg;
        } catch {}
        throw new Error(errMsg);
      }

      const result = await compileRes.json();
      setSyncedWeeks((prev) => [...new Set([...prev, weekToSync])]);
      alert(`Success! Week ${weekToSync} (${program.sport}) compiled & synced to Garmin.\nCheck your calendar.`);
      setSyncStatus("Synced successfully!");
      await loadData();

    } catch (err: any) {
      console.error("Sync error:", err);
      alert(`Failed: ${err.message || "Network/CORS error - check console"}`);
      setSyncStatus("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const [form, setForm] = useState({
    weeks: 8,
    days_per_week: 5,
    session_length: "45-60 min",
    sport: "",
    specific_improvement: "",
    target_metric: "",
    focus: "",
    plan_focus: "",
    selected_movements: [] as string[],
    start_date: format(new Date(), "yyyy-MM-dd"),
    preferred_days: [] as string[],
    injury_considerations: "",
    pbs: {} as Record<string, string>,
    triathlon_distance: "",
    general_fitness_split: "",
  });

  const weeksOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const daysOptions = [1, 2, 3, 4, 5, 6, 7];
  const sessionOptions = ["30-45 min", "45-60 min", "60-90 min", "90+ min"];
  const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const sports = [
    "Running", "Cycling", "Swimming", "Triathlon",
  ];

  const improvementOptions: Record<string, string[]> = {
    Running: ["5km time", "10km time", "Half marathon time", "Marathon time", "Overall pace"],
    Cycling: ["FTP (Functional Threshold Power)", "20-minute power", "5-minute power", "Climbing power", "Overall endurance"],
    Swimming: ["100m time", "400m time", "1500m time", "Stroke efficiency", "Open water endurance"],
    Triathlon: ["Overall race time", "Swim pace", "Bike power", "Run pace", "Transition speed"],
    Weightlifting: ["Snatch max", "Clean & Jerk max", "Back squat max", "Front squat max", "Overhead press max"],
    Powerlifting: ["Squat max", "Bench press max", "Deadlift max", "Total"],
    Bodybuilding: ["Muscle mass (hypertrophy)", "Strength in major lifts", "Symmetry & aesthetics"],
    CrossFit: ["Fran time", "Murph time", "Grace time", "Overall work capacity", "Gymnastics skills"],
    Hyrox: ["Overall race time", "Wall balls", "Sled push", "Rowing", "Farmers carry", "Running efficiency"],
    "General Fitness": ["Overall endurance", "Muscle mass / hypertrophy", "Fat loss", "Recovery / HRV", "Injury prevention / mobility", "Daily energy"],
  };

  const planFocusOptions = [
    "Overall workout capacity",
    "Short time domain workouts",
    "General fitness",
    "Competition prep",
  ];

  const crossFitMovements = [
    "Wall balls", "Box jumps", "Pull-ups", "Toes-to-bar", "Muscle-ups",
    "Handstand push-ups", "Double-unders", "Burpee box jumps", "Thrusters",
    "Clean and jerk", "Snatch", "Deadlift", "Sled push", "Farmers carry",
    "Sandbag lunge", "Rowing", "Ski erg", "Assault bike",
  ];

  const hyroxMovements = [
    "SkiErg", "Sled Push", "Sled Pull", "Burpee Broad Jumps",
    "Rowing", "Farmers Carry", "Sandbag Lunges", "Wall Balls", "Running Under Fatigue",
  ];

  const currentMovements = form.sport === "Hyrox" ? hyroxMovements : crossFitMovements;
  const isCrossFitOrHyrox = form.sport === "CrossFit" || form.sport === "Hyrox";

  const sportPbKeys: Record<string, { key: string; label: string }[]> = {
    Running: [
      { key: "1km_pb", label: "1km PB" },
      { key: "5km_pb", label: "5km PB" },
      { key: "10km_pb", label: "10km PB" },
      { key: "half_marathon_pb", label: "Half Marathon PB" },
      { key: "marathon_pb", label: "Marathon PB" },
    ],
    Cycling: [
      { key: "ftp_pb", label: "FTP" },
      { key: "20min_power_pb", label: "20-minute Power" },
      { key: "5min_power_pb", label: "5-minute Power" },
    ],
    Swimming: [
      { key: "100m_pb", label: "100m PB" },
      { key: "400m_pb", label: "400m PB" },
      { key: "1500m_pb", label: "1500m PB" },
    ],
    Triathlon: [
      { key: "sprint_tri_pb", label: "Sprint Triathlon PB" },
      { key: "olympic_tri_pb", label: "Olympic Triathlon PB" },
      { key: "half_iron_pb", label: "Half Ironman PB" },
      { key: "ironman_pb", label: "Ironman PB" },
      { key: "1km_pb", label: "1km PB" },
      { key: "5km_pb", label: "5km PB" },
      { key: "10km_pb", label: "10km PB" },
      { key: "half_marathon_pb", label: "Half Marathon PB" },
      { key: "marathon_pb", label: "Marathon PB" },
      { key: "ftp_pb", label: "FTP" },
      { key: "20min_power_pb", label: "20-minute Power" },
      { key: "5min_power_pb", label: "5-minute Power" },
      { key: "100m_pb", label: "100m PB" },
      { key: "400m_pb", label: "400m PB" },
      { key: "1500m_pb", label: "1500m PB" },
    ],
    Weightlifting: [
      { key: "snatch_pb", label: "Snatch PB" },
      { key: "clean_jerk_pb", label: "Clean & Jerk PB" },
      { key: "front_squat_pb", label: "Front Squat PB" },
      { key: "overhead_squat_pb", label: "Overhead Squat PB" },
    ],
    Powerlifting: [
      { key: "back_squat_pb", label: "Back Squat PB" },
      { key: "bench_press_pb", label: "Bench Press PB" },
      { key: "deadlift_pb", label: "Deadlift PB" },
    ],
    Bodybuilding: [
      { key: "bodyweight_pb", label: "Current Bodyweight" },
      { key: "lean_mass_pb", label: "Estimated Lean Mass" },
    ],
    CrossFit: [
      { key: "fran_pb", label: "Fran Time" },
      { key: "murph_pb", label: "Murph Time" },
      { key: "grace_pb", label: "Grace Time" },
    ],
    Hyrox: [
      { key: "hyrox_pb", label: "Best Hyrox Time" },
    ],
    "General Fitness": [
      { key: "vo2max_pb", label: "Estimated VO2 Max" },
      { key: "resting_hr_pb", label: "Resting Heart Rate" },
      { key: "body_fat_pb", label: "Body Fat %" },
    ],
  };

  const currentPbFields = sportPbKeys[form.sport] || [];
  const allPbOptions = Object.values(sportPbKeys).flat();

  const sportFunctionMap: Record<string, string> = {
    Running: "create-running-program",
    Cycling: "create-cycling-program",
    Swimming: "create-swimming-program",
    Triathlon: "create-triathlon-program",
    Weightlifting: "create-weightlifting-program",
    Powerlifting: "create-powerlifting-program",
    Bodybuilding: "create-bodybuilding-program",
    CrossFit: "create-crossfit-program",
    Hyrox: "create-hyrox-program",
    "General Fitness": "create-general-fitness-program",
  };

  const loadingMessages = [
    "Creating your program...",
    "Analysing your data...",
    "Building progressions...",
    "Enhancing main sets...",
    "Finalising your plan...",
  ];

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    if (!loading) {
      setCurrentMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => {
        if (prev >= loadingMessages.length - 2) {
          return loadingMessages.length - 1;
        }
        return prev + 1;
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [loading]);

  const roundToPlates = (kg: number, increment = 2.5) =>
    Math.round(kg / increment) * increment;

  const calculateMainLiftLoads = (section: any) => {
    if (!section.pb_key || !section.intensities?.length) return null;

    const pb = pbs.find(p => p.key === section.pb_key)?.value;
    if (!pb) return null;

    const oneRM = parsePbToNumber(section.pb_key, pb);
    if (!oneRM) return null;

    return section.intensities
      .map((pctRaw: number | string) => {
        const pct = Number(pctRaw);
        if (Number.isNaN(pct)) return null;

        const load = (oneRM * pct) / 100;
        const rounded = roundToPlates(load);

        return `${rounded.toFixed(1)} kg`;
      })
      .filter((v): v is string => Boolean(v));
  };

  useEffect(() => {
    loadData();

    let active = true;

    async function setupRealtime() {
      const supabase = getSupabase();
      const user = await currentUser();
      if (!user || !active) return;

      pbChannelRef.current = supabase
        .channel("pb-realtime")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "personal_bests",
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const newPb = payload.new as { key: string; value: string };

            setPbs((prev) => {
              const idx = prev.findIndex((p) => p.key === newPb.key);
              if (idx !== -1) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], value: newPb.value };
                return copy;
              }
              return [...prev, newPb];
            });
          }
        )
        .subscribe();
    }

    setupRealtime();

    return () => {
      active = false;
      if (pbChannelRef.current) {
        getSupabase().removeChannel(pbChannelRef.current);
        pbChannelRef.current = null;
      }
    };
  }, []);

  async function loadData() {
    setDataLoading(true);
    const user = await currentUser();
    if (!user) {
      setDataLoading(false);
      setPageDataLoaded(true);
      return;
    }

    const supabase = getSupabase();

    const { data: pbData } = await supabase
      .from("personal_bests")
      .select("id, key, value")
      .eq("user_id", user.id);

    setPbs(pbData || []);

    try {
      const { data: profile } = await supabase
        .from("clients")
        .select("primary_sport, garmin_access_token")
        .eq("user_id", user.id)
        .single();

      const primary = profile?.primary_sport || "General Fitness";
      setPrimarySport(primary);
      setForm((prev) => ({ ...prev, sport: primary }));

      const { data: programs } = await supabase
        .from("training_programs")
        .select("*")
        .eq("user_id", user.id)
        .limit(1);

      const prog = programs?.[0] ?? null;
      setProgram(prog);

      if (prog) {
        const { data: client } = await supabase
          .from("clients")
          .select("garmin_access_token")
          .eq("user_id", user.id)
          .single();

        setHasGarminConnected(!!client?.garmin_access_token);
      }

      if (prog) {
        const { data: wos } = await supabase
          .from("workouts")
          .select("id, week, day, title, date")
          .eq("program_id", prog.id)
          .order("date", { ascending: true });

        const workoutsWithSections = [];
        for (const w of wos || []) {
          const { data: sections } = await supabase
            .from("workout_sections")
            .select(`
              id,
              section_index,
              name,
              description,
              prescription,
              pb_key,
              exercise,
              sets,
              reps,
              intensities,
              rest_seconds,
              completed
            `)
            .eq("workout_id", w.id)
            .order("section_index");

          workoutsWithSections.push({
            ...w,
            sections: sections || [],
          });
        }

        setWorkouts(workoutsWithSections);
      }
    } catch (err) {
      console.error("Load error:", err);
    } finally {
      setDataLoading(false);
      setPageDataLoaded(true);
    }
  }

  const handleFormChange = (field: keyof typeof form, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "sport") {
      setForm((prev) => ({ ...prev, specific_improvement: "", target_metric: "", plan_focus: "", selected_movements: [], pbs: {} }));
    }
  };

  const handlePbChange = (key: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      pbs: { ...prev.pbs, [key]: value },
    }));
  };

  const saveSinglePb = async (key: string) => {
    const value = form.pbs[key];
    if (!value?.trim()) return;

    const supabase = getSupabase();
    const user = await currentUser();
    if (!user) return;

    try {
      const { data: existing } = await supabase
        .from("personal_bests")
        .select("id")
        .eq("user_id", user.id)
        .eq("key", key)
        .maybeSingle();

      if (existing) {
        await supabase
          .from("personal_bests")
          .update({ value: value.trim() })
          .eq("id", existing.id);
      } else {
        await supabase
          .from("personal_bests")
          .insert({
            user_id: user.id,
            key,
            value: value.trim(),
          });
      }

      setPbs(prev => {
        const existingIndex = prev.findIndex(p => p.key === key);
        const newPb = { key, value: value.trim() };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], value: value.trim() };
          return updated;
        }
        return [...prev, newPb];
      });

      alert(`Saved ${key.replace(/_pb$/, "").toUpperCase()} PB`);
    } catch (err) {
      alert("Failed to save PB");
    }
  };

  const toggleMovement = (movement: string) => {
    setForm((prev) => {
      const selected = prev.selected_movements.includes(movement)
        ? prev.selected_movements.filter((m) => m !== movement)
        : [...prev.selected_movements, movement].slice(0, 3);
      return { ...prev, selected_movements: selected };
    });
  };

  const toggleDay = (day: string) => {
    setForm((prev) => {
      const selected = prev.preferred_days.includes(day)
        ? prev.preferred_days.filter((d) => d !== day)
        : [...prev.preferred_days, day];
      return { ...prev, preferred_days: selected.slice(0, form.days_per_week) };
    });
  };

  const createProgram = async () => {
    if (["CrossFit", "Hyrox"].includes(form.sport)) {
      if (form.selected_movements.length < 3) {
        alert("Please select 3 movements to focus on");
        return;
      }
      if (!form.plan_focus) {
        alert("Please select a training plan focus");
        return;
      }
    }
    if (form.preferred_days.length !== form.days_per_week) {
      alert(`Please select exactly ${form.days_per_week} training days`);
      return;
    }
    setLoading(true);
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      alert("Not authenticated");
      setLoading(false);
      return;
    }
    const user = await currentUser();
    if (!user) return;
    const functionName = sportFunctionMap[form.sport] || "create-general-fitness-program";
    try {
      const res = await fetch(`${ENV.EDGE_BASE}/${functionName}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ ...form, user_id: user.id }),
      });
      if (!res.ok) throw new Error(await res.text());
      await loadData();
      alert("Training program created successfully!");
    } catch (err: any) {
      alert("Failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (pb: { id?: string; key: string; value: string }) => {
    setEditingPb(pb.key);
    setEditValue(pb.value);
  };

  const saveEdit = async () => {
    if (!editingPb || !editValue.trim()) return;

    const supabase = getSupabase();
    const user = await currentUser();
    if (!user) return;

    const existingPb = pbs.find(p => p.key === editingPb);

    try {
      if (existingPb?.id) {
        await supabase
          .from("personal_bests")
          .update({ value: editValue.trim() })
          .eq("id", existingPb.id);
      }

      setPbs(prev => prev.map(p => p.key === editingPb ? { ...p, value: editValue.trim() } : p));
      setEditingPb(null);
      setEditValue("");
    } catch (err) {
      alert("Failed to save PB");
    }
  };

  const cancelEdit = () => {
    setEditingPb(null);
    setEditValue("");
  };

  const addPb = async () => {
    if (!newPbKey || !newPbValue.trim()) return;

    const supabase = getSupabase();
    const user = await currentUser();
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("personal_bests")
        .insert({
          user_id: user.id,
          key: newPbKey,
          value: newPbValue.trim(),
        })
        .select()
        .single();

      if (error) throw error;

      setPbs(prev => [...prev, data]);
      setNewPbKey("");
      setNewPbValue("");
      setAddingPb(false);
    } catch (err) {
      alert("Failed to add PB");
    }
  };

  const currentWorkout = workouts.find(
    (w) => w.date && format(new Date(w.date), "yyyy-MM-dd") === format(currentDate, "yyyy-MM-dd")
  );

  // Fetch workout activity from Garmin
  const fetchWorkoutActivity = async (workoutId: string) => {
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error("Not authenticated");
    }

    try {
      // First check if we have cached activity data
      const { data: cachedActivity } = await supabase
        .from("workout_activities")
        .select("*")
        .eq("workout_id", workoutId)
        .single();

      if (cachedActivity) {
        return cachedActivity;
      }

      // Otherwise fetch from Garmin
      const res = await fetch(`${ENV.EDGE_BASE}/fetch-garmin-activity`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ workout_id: workoutId }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("[Programs] Fetch activity failed:", errorText);
        return null;
      }

      const data = await res.json();
      return data.activity;
    } catch (err) {
      console.error("[Programs] fetchWorkoutActivity error:", err);
      return null;
    }
  };

  const completeSession = async () => {
    if (!currentWorkout) return;

    const supabase = getSupabase();

    try {
      await supabase
        .from("workout_sections")
        .update({ completed: true })
        .eq("workout_id", currentWorkout.id);

      await loadData();
    } catch (err) {
      alert("Failed to complete session");
    }
  };

  const calculateFromPb = (key: string, desc: string) => {
    const pbValue = pbs.find(p => p.key === key)?.value;
    if (!pbValue) return `${desc} (Enter PB)`;
    const match = desc.match(/(\d+)%/);
    if (match) {
      const percent = parseInt(match[1], 10);
      const calculated = (parseFloat(pbValue) * percent) / 100;
      return desc.replace(match[0], `${calculated.toFixed(1)}kg`);
    }
    return desc;
  };

  const getTargetMetricPlaceholder = () => {
    if (!form.specific_improvement) return "e.g. Sub 20:00";
    
    if (form.specific_improvement.includes("time")) {
      return "e.g. Sub 20:00, 3:30:00";
    }
    if (form.specific_improvement.includes("power") || form.specific_improvement.includes("FTP")) {
      return "e.g. 250W, 300W";
    }
    if (form.specific_improvement.includes("pace")) {
      return "e.g. 4:30/km, 5:00/km";
    }
    if (form.specific_improvement.includes("max") || form.specific_improvement.includes("Squat") || form.specific_improvement.includes("Press") || form.specific_improvement.includes("Deadlift")) {
      return "e.g. 100kg, 150kg";
    }
    return "e.g. Sub 20:00";
  };

  // Get top 3-4 PBs to show in header
  const topPbs = pbs.slice(0, 4);

  if (isPageLoading && !pageDataLoaded) {
  return <TrainingProgramLoader onComplete={() => setIsPageLoading(false)} />;
}

// Subscription loading state
if (subscriptionLoading) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
    </div>
  );
}

// Subscription paywall for cancelled users
if (!canAccessPremium) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        <div className="bg-gradient-to-br from-[#f8fafc] to-[#ffffff] rounded-3xl border border-[#e2e8f0] p-8">
          <div className="w-16 h-16 rounded-2xl bg-red-500/20 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-black text-[#0f172a] mb-3">Subscription Required</h1>
          <p className="text-gray-500 mb-6">
            AI Training Programs require an active subscription. Reactivate to create and access your personalized training plans.
          </p>
          <div className="space-y-3">
            <Link
              to="/billing"
              className="flex items-center justify-center gap-2 w-full py-4 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
            >
              Manage Subscription
            </Link>
            <Link
              to="/dashboard"
              className="flex items-center justify-center w-full py-3 bg-gray-800 hover:bg-gray-700 text-gray-600 font-medium rounded-xl transition"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

return (
  <>
      <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
        {/* Header Bar with PB Summary */}
        {program && (
          <header className="sticky top-0 z-40 bg-[#ffffff]/95 backdrop-blur-sm border-b border-[#e2e8f0]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                {/* Left: Back Button */}
                <Link 
                  to="/dashboard" 
                  className="flex items-center gap-2 text-gray-500 hover:text-[#0f172a] transition font-medium"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Link>

                {/* Center: Top PBs (hidden on mobile) */}
                <div className="hidden md:flex items-center gap-4">
                  {topPbs.map((pb, idx) => (
                    <div 
                      key={pb.id || pb.key}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-800/50 rounded-lg border border-gray-700/50"
                    >
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-gray-500">
                        {PB_LABELS[pb.key] || pb.key}
                      </span>
                      <span className="text-sm font-bold text-[#0f172a]">{pb.value}</span>
                    </div>
                  ))}
                </div>

                {/* Right: View All PBs Button */}
                <button
                  onClick={() => setPbPanelOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl transition font-medium text-sm"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>Personal Bests</span>
                  <span className="text-xs text-gray-500">({pbs.length})</span>
                </button>
              </div>
            </div>
          </header>
        )}

        {/* Main Content - Centered */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {!program ? (
            <div className="mt-6">
              <div className="mb-6">
                <Link to="/dashboard" className="flex items-center gap-3 text-gray-500 hover:text-[#0f172a] transition font-medium w-fit">
                  <ChevronLeft className="w-6 h-6" />
                  Back to Dashboard
                </Link>
              </div>
              <div className="text-center mb-12">
                <h1 className="text-4xl md:text-5xl font-bold mb-3">Create Your Training Program</h1>
                <p className="text-gray-500 text-base md:text-lg">
                  Personalized plan based on your sport and goals
                </p>
              </div>

              <div className="space-y-8">
                {/* Program Basics */}
                <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-6 md:p-8 border border-gray-800/50">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-sm font-bold">1</span>
                    Program Basics
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-base font-semibold mb-2">Program Length</label>
                      <p className="text-xs text-gray-500 mb-3">How many weeks?</p>
                      <select
                        value={form.weeks}
                        onChange={(e) => handleFormChange("weeks", parseInt(e.target.value))}
                        className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                      >
                        {weeksOptions.map((w) => (
                          <option key={w} value={w}>{w} week{w > 1 ? "s" : ""}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-base font-semibold mb-2">Training Days per Week</label>
                      <p className="text-xs text-gray-500 mb-3">Days you'll train</p>
                      <select
                        value={form.days_per_week}
                        onChange={(e) => handleFormChange("days_per_week", parseInt(e.target.value))}
                        className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                      >
                        {daysOptions.map((d) => (
                          <option key={d} value={d}>{d} day{d > 1 ? "s" : ""}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-base font-semibold mb-2">Session Length</label>
                      <p className="text-xs text-gray-500 mb-3">How long per session?</p>
                      <select
                        value={form.session_length}
                        onChange={(e) => handleFormChange("session_length", e.target.value)}
                        className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                      >
                        {sessionOptions.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Schedule Section */}
                <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-6 md:p-8 border border-gray-800/50">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-sm font-bold">2</span>
                    Schedule
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-base font-semibold mb-2">Start Date</label>
                      <p className="text-xs text-gray-500 mb-3">When do you want to begin?</p>
                      <input
                        type="date"
                        value={form.start_date}
                        min={format(new Date(), "yyyy-MM-dd")}
                        onChange={(e) => handleFormChange("start_date", e.target.value)}
                        className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                      />
                    </div>

                    <div>
                      <label className="block text-base font-semibold mb-2">
                        Training Days (select {form.days_per_week})
                      </label>
                      <p className="text-xs text-gray-500 mb-3">Which days work best for you?</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {weekdays.map((day) => (
                          <button
                            key={day}
                            onClick={() => toggleDay(day)}
                            disabled={form.preferred_days.length >= form.days_per_week && !form.preferred_days.includes(day)}
                            className={`p-3 rounded-xl border transition font-medium ${
                              form.preferred_days.includes(day)
                                ? "bg-green-500 text-[#0f172a] border-green-500"
                                : "bg-gray-800/50 border-gray-700 hover:border-green-500"
                            } ${form.preferred_days.length >= form.days_per_week && !form.preferred_days.includes(day) ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sport Selection */}
                <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-6 md:p-8 border border-gray-800/50">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-sm font-bold">3</span>
                    Sport & Specialization
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-base font-semibold mb-2">Your Sport</label>
                      <p className="text-xs text-gray-500 mb-3">What do you train for?</p>
                      <select
                        value={form.sport}
                        onChange={(e) => handleFormChange("sport", e.target.value)}
                        className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                      >
                        <option value="">Select your sport</option>
                        {sports.map((s) => (
                          <option key={s} value={s}>
                            {s} {s === primarySport && "(Your Primary)"}
                          </option>
                        ))}
                      </select>
                    </div>

                    {form.sport === "General Fitness" && (
                      <div>
                        <label className="block text-base font-semibold mb-2">Training Split</label>
                        <p className="text-xs text-gray-500 mb-3">Choose your preferred style</p>
                        <select
                          value={form.general_fitness_split}
                          onChange={(e) => handleFormChange("general_fitness_split", e.target.value)}
                          className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                        >
                          <option value="">Select your preferred split...</option>
                          <option value="upper_lower">Upper/Lower Split</option>
                          <option value="push_pull_legs">Push/Pull/Legs (PPL)</option>
                          <option value="full_body">Full Body</option>
                          <option value="strength_conditioning">Strength & Conditioning</option>
                          <option value="bodyweight">Bodyweight Only</option>
                        </select>
                      </div>
                    )}

                    {form.sport === "Triathlon" && (
                      <div>
                        <label className="block text-base font-semibold mb-2">Target Race Distance</label>
                        <p className="text-xs text-gray-500 mb-3">What distance are you training for?</p>
                        <select
                          value={form.triathlon_distance}
                          onChange={(e) => handleFormChange("triathlon_distance", e.target.value)}
                          className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                        >
                          <option value="">Select distance...</option>
                          <option value="sprint">Sprint</option>
                          <option value="olympic">Olympic</option>
                          <option value="70.3">Half Ironman (70.3)</option>
                          <option value="ironman">Ironman</option>
                        </select>
                      </div>
                    )}

                    {!isCrossFitOrHyrox && form.sport && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-base font-semibold mb-2">
                            Specific Goal
                          </label>
                          <p className="text-xs text-gray-500 mb-3">What do you want to improve most?</p>
                          <select
                            value={form.specific_improvement}
                            onChange={(e) => handleFormChange("specific_improvement", e.target.value)}
                            className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                          >
                            <option value="">Select a metric...</option>
                            {(improvementOptions[form.sport] || improvementOptions["General Fitness"]).map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        {form.specific_improvement && (
                          <div>
                            <label className="block text-base font-semibold mb-2">
                              Target Time/Metric
                            </label>
                            <p className="text-xs text-gray-500 mb-3">What's your goal? (Optional)</p>
                            <input
                              type="text"
                              placeholder={getTargetMetricPlaceholder()}
                              value={form.target_metric}
                              onChange={(e) => handleFormChange("target_metric", e.target.value)}
                              className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Personal Bests */}
                {form.sport && currentPbFields.length > 0 && (
                  <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-6 md:p-8 border border-gray-800/50">
                    <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-sm font-bold">4</span>
                      Personal Bests
                    </h2>
                    <p className="text-sm text-gray-500 mb-6">
                      These will be used to personalize your training. Existing PBs are pre-filled.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {currentPbFields.map(({ key, label }) => {
                        const existingValue = pbValuesByKey[key] || form.pbs[key] || "";

                        return (
                          <div key={key} className="flex flex-col gap-2">
                            <label className="text-sm font-semibold">{label}</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder={`e.g. 350, 19:08`}
                                value={form.pbs[key] ?? existingValue}
                                onChange={(e) => handlePbChange(key, e.target.value)}
                                className="flex-1 px-4 py-2.5 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm"
                              />
                              <button
                                onClick={() => saveSinglePb(key)}
                                disabled={!form.pbs[key]?.trim()}
                                className={`px-4 py-2.5 rounded-xl font-semibold transition flex items-center justify-center gap-1.5 text-sm whitespace-nowrap ${
                                  form.pbs[key]?.trim() 
                                    ? 'bg-green-500 hover:bg-green-400 text-[#0f172a]' 
                                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                }`}
                              >
                                <CheckCircle className="w-4 h-4" />
                                Save
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* CrossFit/Hyrox Specific */}
                {isCrossFitOrHyrox && (
                  <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-6 md:p-8 border border-gray-800/50">
                    <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-sm font-bold">4</span>
                      {form.sport} Specifics
                    </h2>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-base font-semibold mb-2">Training Plan Focus</label>
                        <p className="text-xs text-gray-500 mb-3">What's your main objective?</p>
                        <select
                          value={form.plan_focus}
                          onChange={(e) => handleFormChange("plan_focus", e.target.value)}
                          className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition"
                        >
                          <option value="">Select focus</option>
                          {planFocusOptions.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-base font-semibold mb-2">
                          Focus Movements ({form.selected_movements.length}/3)
                        </label>
                        <p className="text-xs text-gray-500 mb-3">Choose 3 movements to prioritize</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {currentMovements.map((move) => (
                            <button
                              key={move}
                              onClick={() => toggleMovement(move)}
                              disabled={form.selected_movements.length >= 3 && !form.selected_movements.includes(move)}
                              className={`p-3 rounded-lg border transition text-sm font-medium ${
                                form.selected_movements.includes(move)
                                  ? "bg-green-500 text-[#0f172a] border-green-500"
                                  : "bg-gray-800/50 border-gray-700 hover:border-green-500"
                              } ${form.selected_movements.length >= 3 && !form.selected_movements.includes(move) ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              {move}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Final Details */}
                <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-6 md:p-8 border border-gray-800/50">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-sm font-bold">{form.sport && currentPbFields.length > 0 ? "5" : isCrossFitOrHyrox ? "5" : "4"}</span>
                    Final Details
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-base font-semibold mb-2">Injuries or Limitations</label>
                      <p className="text-xs text-gray-500 mb-3">Any issues we should work around?</p>
                      <textarea
                        placeholder="e.g. Knee pain on impact, shoulder impingement..."
                        value={form.injury_considerations}
                        onChange={(e) => handleFormChange("injury_considerations", e.target.value)}
                        rows={4}
                        className="w-full p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none resize-none transition"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={createProgram}
                  disabled={
                    loading ||
                    form.preferred_days.length !== form.days_per_week ||
                    (isCrossFitOrHyrox && (form.selected_movements.length < 3 || !form.plan_focus))
                  }
                  className="relative w-full py-4 bg-green-500 hover:bg-green-400 disabled:opacity-60 disabled:cursor-not-allowed text-[#0f172a] font-bold text-lg rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating Program...
                    </>
                  ) : (
                    "Create My Program"
                  )}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Sync to Garmin Button */}
              {program && program.current_week < program.weeks_total && (
                <div className="flex flex-col items-center mb-8 gap-3">
                  {syncedWeeks.includes(program.current_week) ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="px-8 py-4 rounded-2xl font-bold text-lg bg-gray-700 text-gray-600 shadow-sm cursor-not-allowed flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        Workouts Synced to Garmin
                      </div>
                      <p className="text-xs text-gray-500 text-center">
                        Powered by Garmin Connect • Check your calendar
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={handleSyncToGarmin}
                      disabled={syncing || !hasGarminConnected}
                      className={`
                        px-8 py-4 rounded-2xl font-bold text-lg transition-all shadow-lg flex items-center gap-3
                        ${syncing
                          ? "bg-gray-700 cursor-wait text-gray-600"
                          : hasGarminConnected
                            ? "bg-green-600 hover:bg-green-500 text-[#0f172a] shadow-green-500/30"
                            : "bg-gray-700 text-gray-500 cursor-not-allowed"
                        }
                      `}
                    >
                      {syncing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {syncStatus || "Syncing to Garmin..."}
                        </>
                      ) : hasGarminConnected ? (
                        "Sync Week to Garmin"
                      ) : (
                        "Connect Garmin to Sync"
                      )}
                    </button>
                  )}

                  {hasGarminConnected && (
                    <p className="text-xs text-gray-500 text-center">
                      Powered by Garmin Connect • Workouts appear in your calendar
                    </p>
                  )}
                </div>
              )}

              {/* Program Actions Menu */}
              <div className="relative flex justify-center mb-8">
                <button
                  onClick={() => setShowProgramMenu(!showProgramMenu)}
                  className="p-3 bg-gray-800 hover:bg-gray-700 rounded-full transition text-gray-500 hover:text-[#0f172a]"
                  aria-label="Program options"
                >
                  <MoreHorizontal className="w-6 h-6" />
                </button>

                {showProgramMenu && (
                  <div className="absolute top-full right-0 mt-2 bg-[#f8fafc] border border-gray-700 rounded-xl shadow-xl z-20 min-w-[180px] overflow-hidden">
                    <button
                      onClick={() => {
                        setShowProgramMenu(false);
                        setShowDeleteConfirm(true);
                      }}
                      className="w-full px-5 py-3 text-left text-red-400 hover:bg-red-900/30 transition flex items-center gap-3"
                    >
                      <CloseIcon className="w-5 h-5" />
                      Delete Program
                    </button>
                  </div>
                )}
              </div>

               {/* ========== CALENDAR STRIP NAVIGATION ========== */}
              <CalendarStrip
                currentDate={currentDate}
                onSelectDate={setCurrentDate}
                workouts={workouts}
                program={program}
              />

              {/* Delete Confirmation */}
              {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                  <div className="bg-[#f8fafc] p-8 rounded-2xl max-w-md w-full text-center border border-gray-700">
                    <h3 className="text-2xl font-bold text-red-400 mb-4">Delete Program?</h3>
                    <p className="text-gray-600 mb-6">
                      This will permanently delete "{program?.name}" and all its workouts, progress, and data. This cannot be undone.
                    </p>
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-[#0f172a] font-medium rounded-xl transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          if (!program?.id) return;
                          setLoading(true);
                          try {
                            const supabase = getSupabase();
                            const { error } = await supabase
                              .from("training_programs")
                              .delete()
                              .eq("id", program.id);
                            if (error) throw error;

                            await supabase.from("workouts").delete().eq("program_id", program.id);
                            await supabase.from("workout_sections").delete().eq("workout_id", program.id);
                            await supabase.from("planned_workouts").delete().eq("workout_id", program.id);

                            setProgram(null);
                            setWorkouts([]);
                            setSyncedWeeks([]);
                            setShowDeleteConfirm(false);
                            alert("Program deleted successfully.");
                          } catch (err: any) {
                            console.error("Delete program failed:", err);
                            alert(`Failed to delete: ${err.message}`);
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className="px-6 py-3 bg-red-600 hover:bg-red-700 text-[#0f172a] font-bold rounded-xl transition shadow-lg"
                      >
                        Delete Forever
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Workout Sections */}
              {currentWorkout ? (
                currentWorkout.sections && currentWorkout.sections.length > 0 ? (
                  <div className="space-y-8">
                    {currentWorkout.sections.map((sec: any, i: number) => {
                      const displayDesc = sec.pb_key
                        ? calculateFromPb(sec.pb_key, sec.description || sec.desc || "")
                        : (sec.description || sec.desc || "");

                      const isMainLift = sec.name === "Main Lift";
                      const loads = isMainLift ? calculateMainLiftLoads(sec) : null;

                      return (
                        <div
                          key={sec.id || i}
                          className="w-full bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-3xl shadow-2xl hover:shadow-green-500/20 hover:-translate-y-1 transition-all duration-300 overflow-hidden border border-gray-800/50"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 sm:p-6 border-b border-black/5">
                            <div className="flex items-center gap-4 mb-4 sm:mb-0">
                              <button
                                onClick={async () => {
                                  const supabase = getSupabase();
                                  const newChecked = !sec.completed;

                                  try {
                                    await supabase
                                      .from("workout_sections")
                                      .update({ completed: newChecked })
                                      .eq("id", sec.id);

                                    setWorkouts(prevWorkouts =>
                                      prevWorkouts.map(w =>
                                        w.id === currentWorkout.id
                                          ? {
                                              ...w,
                                              sections: w.sections.map(s =>
                                                s.id === sec.id ? { ...s, completed: newChecked } : s
                                              )
                                            }
                                          : w
                                      )
                                    );
                                  } catch (err) {
                                    console.error("Failed to update completion", err);
                                    alert("Failed to save completion status");
                                  }
                                }}
                                className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                                  sec.completed 
                                    ? 'bg-green-500 hover:bg-green-400 shadow-lg shadow-green-500/50' 
                                    : 'bg-gray-800 hover:bg-gray-700 border-2 border-gray-600'
                                }`}
                              >
                                {sec.completed && (
                                  <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-[#0f172a]" strokeWidth={2.5} />
                                )}
                              </button>
                              <h3 className="text-2xl sm:text-3xl font-black text-[#0f172a] leading-tight tracking-tight">
                                {sec.name || "Unnamed Section"}
                              </h3>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => toggleDetails(i)}
                                className="text-green-400 hover:text-green-300 transition text-sm sm:text-base font-medium"
                              >
                                {openSections[i] ? "Hide Details" : "View Details"}
                              </button>
                              <button className="flex items-center gap-2 text-green-400 hover:text-green-300 transition text-sm sm:text-base">
                                <Loader2 className="w-5 h-5" />
                                <span className="font-medium hidden sm:inline">History</span>
                              </button>
                            </div>
                          </div>

                          <div className="px-8 py-10 space-y-6">
                            {isMainLift ? (
                              <>
                                <div className="text-lg text-gray-500">
                                  {sec.exercise} — {sec.sets} × {sec.reps}
                                </div>

                                {loads ? (
                                  <div className="space-y-1">
                                    {loads.map((load: string, idx: number) => (
                                      <div
                                        key={idx}
                                        className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0f172a]-400"
                                      >
                                        {idx + 1} × {load}
                                      </div>
                                    ))}
                                  </div>
                                ) : sec.pb_key ? (
                                  <div className="space-y-2 pt-2">
                                    <p className="text-gray-500 text-sm">
                                      Enter your PB to calculate working weights
                                    </p>
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        placeholder="e.g. 150 or 19:08"
                                        value={tempPbInputs[sec.pb_key] || ""}
                                        onChange={(e) =>
                                          setTempPbInputs(prev => ({
                                            ...prev,
                                            [sec.pb_key]: e.target.value,
                                          }))
                                        }
                                        className="flex-1 bg-black/5 border border-black/10 rounded-xl px-4 py-3 text-[#0f172a] focus:border-green-500 outline-none"
                                      />
                                      <button
                                        onClick={async () => {
                                          const value = tempPbInputs[sec.pb_key];
                                          if (!value) return;
                                          const supabase = getSupabase();
                                          const user = await currentUser();
                                          if (!user) return;
                                          await supabase.from("personal_bests").upsert({
                                            user_id: user.id,
                                            key: sec.pb_key,
                                            value,
                                          });
                                          setTempPbInputs(prev => {
                                            const copy = { ...prev };
                                            return copy;
                                          });
                                        }}
                                        className="px-5 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                ) : null}

                                {sec.rest_seconds && (
                                  <div className="text-sm text-gray-500">
                                    Rest {Math.floor(sec.rest_seconds / 60)}:
                                    {(sec.rest_seconds % 60).toString().padStart(2, "0")}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f172a] whitespace-pre-wrap tracking-tight leading-relaxed">
                                {sec.prescription || "No prescription"}
                              </div>
                            )}
                          </div>

                          {openSections[i] && displayDesc && (
                            <div className="px-8 pb-6">
                              <p className="text-base sm:text-lg text-gray-600 leading-relaxed whitespace-pre-wrap">
                                {displayDesc}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 pt-4">
                      <button
                        onClick={completeSession}
                        className="w-full sm:w-auto min-w-[200px] px-8 py-4 bg-green-600 hover:bg-green-500 text-[#0f172a] font-bold rounded-xl transition-all duration-200 text-base shadow-lg hover:shadow-green-500/30"
                      >
                        Complete Session
                      </button>
                    </div>
                 
                    {/* ========== WORKOUT STATS PANEL ========== */}
                    <div className="mt-6 md:mt-8">
                      <WorkoutStatsPanel
                        workoutId={currentWorkout.id}
                        workoutTitle={currentWorkout.title}
                        workoutDate={currentWorkout.date}
                        programName={program?.name}
                        sport={program?.sport}
                        weekNumber={currentWorkout.week}
                        dayNumber={currentWorkout.day}
                        initialActivity={workoutActivity}
                        onFetchActivity={async () => {
                          const activity = await fetchWorkoutActivity(currentWorkout.id);
                          if (activity) setWorkoutActivity(activity);
                          return activity;
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 text-lg md:text-xl mt-20">
                    Workout has no sections defined
                  </div>
                )
              ) : (
                <div className="text-center text-gray-500 text-xl mt-32">
                  No workout scheduled for today
                </div>
              )}
            </>
          )}
        </main>

        {/* PB Slide-in Panel */}
        {pbPanelOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              onClick={() => setPbPanelOpen(false)}
            />

            {/* Panel */}
            <aside className="fixed top-0 right-0 h-screen w-full sm:w-[500px] bg-[#ffffff] border-l border-[#e2e8f0] z-50 overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-[#ffffff] border-b border-[#e2e8f0] p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold">Personal Bests</h2>
                <button
                  onClick={() => setPbPanelOpen(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {pbs.length > 0 ? (
                  <>
                    {pbs.map((pb) => (
                      <div key={pb.id || pb.key} className="bg-[#f8fafc] rounded-2xl p-5 border border-[#e2e8f0] hover:border-gray-700 transition">
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">
                              {PB_LABELS[pb.key] || pb.key.replace(/_/g, " ").replace(/_pb$/, "").trim()}
                            </p>
                            {editingPb === pb.key ? (
                              <div className="flex items-center gap-2 mt-2">
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                                  className="bg-gray-800 text-[#0f172a] px-3 py-2 rounded-lg border border-gray-600 focus:border-green-500 outline-none flex-1"
                                  autoFocus
                                />
                                <button onClick={saveEdit} className="text-green-400 hover:text-green-300 p-2">
                                  <CheckCircle className="w-5 h-5" />
                                </button>
                                <button onClick={cancelEdit} className="text-red-400 hover:text-red-300 p-2">
                                  <CloseIcon className="w-5 h-5" />
                                </button>
                              </div>
                            ) : (
                              <p
                                onClick={() => startEdit(pb)}
                                className="text-2xl font-bold text-[#0f172a] mt-1 cursor-pointer hover:text-green-400 transition"
                              >
                                {pb.value}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-gray-500 text-center py-12">No personal bests recorded yet</p>
                )}

                {addingPb ? (
                  <div className="mt-6 bg-[#f8fafc] rounded-2xl p-5 border border-[#e2e8f0]">
                    <select
                      value={newPbKey}
                      onChange={(e) => setNewPbKey(e.target.value)}
                      className="w-full bg-gray-800 text-[#0f172a] px-4 py-3 rounded-lg border border-gray-600 focus:border-green-500 outline-none mb-3"
                    >
                      <option value="">Select PB type...</option>
                      {allPbOptions.map(opt => (
                        <option key={opt.key} value={opt.key}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Value (e.g. 19:08, 150kg)"
                      value={newPbValue}
                      onChange={(e) => setNewPbValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addPb()}
                      className="w-full bg-gray-800 text-[#0f172a] px-4 py-3 rounded-lg border border-gray-600 focus:border-green-500 outline-none mb-3"
                    />
                    <div className="flex gap-2">
                      <button onClick={addPb} className="flex-1 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold py-3 rounded-lg transition">
                        Add
                      </button>
                      <button onClick={() => { setAddingPb(false); setNewPbKey(""); setNewPbValue(""); }} className="flex-1 bg-gray-700 hover:bg-gray-600 text-[#0f172a] font-bold py-3 rounded-lg transition">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingPb(true)}
                    className="w-full mt-4 py-4 border-2 border-dashed border-gray-600 rounded-2xl text-gray-500 hover:border-green-500 hover:text-green-400 transition font-medium flex items-center justify-center gap-2"
                  >
                    <AddIcon className="w-5 h-5" />
                    Add New Record
                  </button>
                )}
              </div>
            </aside>
          </>
        )}
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center space-y-8">
            <div className="animate-pulse">
              <Loader2 className="w-20 h-20 text-green-500 mx-auto animate-spin" />
            </div>
            <p className="text-3xl md:text-4xl font-bold text-[#0f172a] animate-fade-in">
              {loadingMessages[currentMessageIndex]}
            </p>
            <p className="text-lg text-gray-600">
              This may take 10-20 seconds...
            </p>
          </div>
        </div>
      )}
    </>
  );
}
